/*
 * Broadcast Theme — custom.js
 * Keep customizations here to simplify future theme updates.
 * To activate: uncomment the custom.js import in layout/theme.liquid
 */
(function () {
  const idle = window.requestIdleCallback
    ? (fn) => window.requestIdleCallback(fn, { timeout: 300 })
    : (fn) => window.setTimeout(fn, 50);

  function addSwipe(el, { threshold = 40, onLeft, onRight } = {}) {
    if (!el) return;
    let startX = null, startY = null;
    el.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    el.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      startX = null;
      if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0 && onLeft) onLeft();
      if (dx > 0 && onRight) onRight();
    }, { passive: true });
  }

  function createTicker(items, speed) {
    if (items.length < 2) return null;
    items[0].classList.add('is-active');
    let index = 0;
    return setInterval(() => {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, speed);
  }

  class SlidingNavAnimation {
    constructor(nav, focus) {
      this.nav = nav;
      this.focus = focus;
      this.links = Array.from(nav.querySelectorAll('.menu__item'));
      if (this.links.length) this.init();
    }
    init() {
      this.setPosition(this.links[0]);
      this.links.forEach((link) => link.addEventListener('mouseover', () => this.setPosition(link, true)));
      this.nav.addEventListener('mouseleave', () => { this.focus.style.opacity = '0'; });
    }
    setPosition(target, show = false) {
      const f = this.focus;
      f.style.width = `${target.offsetWidth}px`;
      f.style.height = `${target.offsetHeight}px`;
      f.style.top = `${target.offsetTop + target.offsetHeight / 2}px`;
      f.style.left = `${target.offsetLeft + target.offsetWidth / 2}px`;
      f.style.transform = 'translate(-50%, -50%)';
      if (show) f.style.opacity = '1';
    }
  }

  class TextFloatAnimation {
    constructor(container) {
      this.container = container;
      this.floatImages = Array.from(container.querySelectorAll('.float-image-wrapper'));
      if (this.floatImages.length && window.gsap && window.ScrollTrigger) this.init();
    }
    init() {
      const tl = window.gsap.timeline({
        scrollTrigger: { trigger: this.container, start: 'top bottom', end: 'bottom top', scrub: 1.5 }
      });
      this.floatImages.forEach((image, i) => {
        window.gsap.set(image, { yPercent: 0 });
        tl.to(image, {
          yPercent: window.gsap.utils.random(-150, -90),
          rotate: window.gsap.utils.random(-30, 30),
          scale: 1.15,
          ease: 'none'
        }, i * 0.05);
      });
    }
  }

  class ProductCollectionShowcase {
    constructor(section) {
      this.section = section;
      this.panels = Array.from(section.querySelectorAll('[data-product-panel]'));
      this.items = Array.from(section.querySelectorAll('[data-showcase-item]'));
      this.prevButton = section.querySelector('[data-showcase-prev]');
      this.nextButton = section.querySelector('[data-showcase-next]');
      this.tickerItems = Array.from(section.querySelectorAll('[data-showcase-ticker-item]'));
      this.tickerRotationSpeed = parseInt(section.dataset.rotationSpeed, 10) || 3000;
      this.activeIndex = 0;
      if (!this.items.length) return;
      this.activeIndex = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.init();
    }
    init() {
      this.items.forEach((item) => {
        item.addEventListener('click', () => {
          const index = parseInt(item.dataset.productIndex, 10);
          if (!Number.isNaN(index)) this.setActiveItem(index, true);
        });
      });
      this.prevButton?.addEventListener('click', () => this.goToPrevious());
      this.nextButton?.addEventListener('click', () => this.goToNext());
      const swipeTarget = this.section.querySelector('[data-showcase-items]') || this.section;
      addSwipe(swipeTarget, { onLeft: () => this.goToNext(), onRight: () => this.goToPrevious() });
      this.setActiveItem(this.activeIndex, false);
      createTicker(this.tickerItems, this.tickerRotationSpeed);
    }
    normalizeIndex(index) {
      const total = this.items.length;
      if (!total) return 0;
      if (index < 0) return total - 1;
      if (index >= total) return 0;
      return index;
    }
    getRelativePosition(activeIndex, itemIndex) {
      const total = this.items.length;
      let rel = itemIndex - activeIndex;
      if (rel > total / 2) rel -= total;
      if (rel < -total / 2) rel += total;
      return rel;
    }
    setActiveItem(index, triggerPanelEvent = false) {
      this.activeIndex = this.normalizeIndex(index);
      const posMap = { 0: 'is-pos-0', '-1': 'is-pos-neg-1', '-2': 'is-pos-neg-2', '-3': 'is-pos-neg-3', 1: 'is-pos-pos-1', 2: 'is-pos-pos-2', 3: 'is-pos-pos-3' };
      const posClasses = Object.values(posMap);
      this.items.forEach((item, i) => {
        const rel = this.getRelativePosition(this.activeIndex, i);
        item.classList.remove(...posClasses, 'is-hidden', 'is-active');
        const cls = posMap[rel];
        if (cls) {
          item.classList.add(cls);
          if (rel === 0) item.classList.add('is-active');
        } else {
          item.classList.add('is-hidden');
        }
        item.setAttribute('aria-current', rel === 0 ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        panel.classList.toggle('is-active', parseInt(panel.dataset.productIndex, 10) === this.activeIndex);
      });
      if (triggerPanelEvent) {
        const productInfo = this.panels[this.activeIndex]?.querySelector('product-info');
        if (productInfo) {
          document.dispatchEvent(new CustomEvent('theme:product:panel:change', {
            detail: { sectionId: this.section.dataset.sectionId, productId: productInfo.dataset.productId }
          }));
        }
      }
    }
    goToPrevious() { this.setActiveItem(this.activeIndex - 1, true); }
    goToNext() { this.setActiveItem(this.activeIndex + 1, true); }
  }

  class CircularCards {
    constructor(section) {
      this.section = section;
      this.ring = section.querySelector('[data-ring]');
      this.cards = Array.from(section.querySelectorAll('.ring-card'));
      this.prevBtn = section.querySelector('[data-circular-prev]');
      this.nextBtn = section.querySelector('[data-circular-next]');
      this.activeIndex = parseInt(section.dataset.initialIndex || 0, 10) || 0;
      if (!this.ring || !this.cards.length) return;
      this.ro = new ResizeObserver(() => this.update());
      this.prepareMedia();
      this.bindEvents();
      this.update();
    }
    bindEvents() {
      this.prevBtn?.addEventListener('click', () => {
        this.activeIndex = (this.activeIndex - 1 + this.cards.length) % this.cards.length;
        this.update();
      });
      this.nextBtn?.addEventListener('click', () => {
        this.activeIndex = (this.activeIndex + 1) % this.cards.length;
        this.update();
      });
      this.cards.forEach((card, index) => {
        const handler = () => {
          if (index !== this.activeIndex) {
            this.activeIndex = index;
            this.update();
          } else {
            this.toggleVideo(card);
          }
        };
        card.addEventListener('click', handler);
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(); }
        });
      });
      this.ro.observe(this.ring);
      addSwipe(this.ring, {
        onLeft: () => { this.activeIndex = (this.activeIndex + 1) % this.cards.length; this.update(); },
        onRight: () => { this.activeIndex = (this.activeIndex - 1 + this.cards.length) % this.cards.length; this.update(); }
      });
    }
    prepareMedia() {
      this.cards.forEach((card) => {
        const iframe = card.querySelector('iframe[data-src]');
        const video = card.querySelector('video');
        if (iframe) iframe.src = '';
        if (video) { video.muted = true; video.loop = true; video.playsInline = true; }
      });
    }
    toggleVideo(card) {
      if (card.classList.contains('is-video-open')) {
        card.classList.remove('is-video-open');
        const video = card.querySelector('video');
        const iframe = card.querySelector('iframe');
        if (video) { video.pause(); video.currentTime = 0; }
        if (iframe) iframe.src = '';
        return;
      }
      card.classList.add('is-video-open');
      const video = card.querySelector('video');
      const iframe = card.querySelector('iframe[data-src]');
      if (video) video.play()?.catch(() => {});
      if (iframe?.dataset.src && !iframe.src) iframe.src = iframe.dataset.src;
    }
    update() {
      const total = this.cards.length;
      if (!total) return;

      const isMobile = window.innerWidth < 750;

      const frontY = isMobile ? 36 : 72;

      const nearX = isMobile ? 92 : 180;
      const nearY = isMobile ? 4 : 6;

      const farX = isMobile ? 118 : 245;
      const farY = isMobile ? -42 : -92;

      const scaleNear = isMobile ? 0.78 : 0.86;
      const scaleFar = isMobile ? 0.66 : 0.76;

      this.cards.forEach((card, i) => {
        let rel = i - this.activeIndex;
        if (rel > total / 2) rel -= total;
        if (rel < -total / 2) rel += total;

        const abs = Math.abs(rel);
        const hidden = abs > 2;

        card.classList.toggle('is-front', rel === 0);
        card.classList.toggle('is-hidden', hidden);

        if (hidden) {
          card.style.cssText = 'transform:translate(-50%,-50%) scale(0.6);opacity:0;z-index:1;pointer-events:none';
          return;
        }

        let x = 0, y = 0, scale = 1, z = 60;

        if (rel === 0) {
          y = frontY;
          scale = isMobile ? 0.9 : 1;
        } else if (abs === 1) {
          x = (rel < 0 ? -1 : 1) * nearX;
          y = nearY;
          scale = scaleNear;
          z = 36;
        } else {
          x = (rel < 0 ? -1 : 1) * farX;
          y = farY;
          scale = scaleFar;
          z = 0;
        }

        card.style.cssText = `transform:translate(-50%,-50%) translate(${x}px,${y}px) scale(${scale});opacity:1;z-index:${z};pointer-events:auto`;
      });
    }
  }

  class CustomTabs {
    constructor(section) {
      this.section = section;
      this.triggers = Array.from(section.querySelectorAll('[data-tab-trigger]'));
      this.panels = Array.from(section.querySelectorAll('[data-tab-panel]'));
      if (this.triggers.length && this.panels.length) this.init();
    }
    init() {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.setActiveTab(Number(trigger.getAttribute('data-tab-index'))));
        trigger.addEventListener('keydown', (e) => this.handleKeydown(e, trigger));
      });
      const panelContainer = this.section.querySelector('[data-tab-panels]') || this.section;
      addSwipe(panelContainer, {
        onLeft: () => {
          const cur = this.triggers.findIndex((t) => t.getAttribute('aria-selected') === 'true');
          this.setActiveTab(cur + 1 >= this.triggers.length ? 0 : cur + 1);
        },
        onRight: () => {
          const cur = this.triggers.findIndex((t) => t.getAttribute('aria-selected') === 'true');
          this.setActiveTab(cur - 1 < 0 ? this.triggers.length - 1 : cur - 1);
        }
      });
    }
    setActiveTab(index) {
      this.triggers.forEach((t) => {
        const active = Number(t.getAttribute('data-tab-index')) === index;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.setAttribute('tabindex', active ? '0' : '-1');
      });
      this.panels.forEach((p) => {
        const active = Number(p.getAttribute('data-tab-index')) === index;
        p.classList.toggle('is-active', active);
        p.toggleAttribute('hidden', !active);
      });
    }
    handleKeydown(e, trigger) {
      const cur = this.triggers.indexOf(trigger);
      let next = cur;
      if (e.key === 'ArrowRight') next = cur + 1 >= this.triggers.length ? 0 : cur + 1;
      if (e.key === 'ArrowLeft') next = cur - 1 < 0 ? this.triggers.length - 1 : cur - 1;
      if (next !== cur) { e.preventDefault(); this.setActiveTab(next); this.triggers[next].focus(); }
    }
  }

  class ReviewGalleryShowcase {
    constructor(section) {
      this.section = section;
      this.layout = section.dataset.layout;
      this.items = Array.from(section.querySelectorAll('[data-review-gallery-item]'));
      this.track = section.querySelector('[data-review-gallery-track]');
      this.viewport = section.querySelector('[data-review-gallery-viewport]');
      this.prevButton = section.querySelector('[data-review-gallery-prev]');
      this.nextButton = section.querySelector('[data-review-gallery-next]');
      this.tickerItems = Array.from(section.querySelectorAll('[data-review-gallery-ticker-item]'));
      this.tickerSpeed = parseInt(section.dataset.tickerSpeed, 10) || 3000;
      this.currentIndex = 0;
      this.visibleSlides = 3;
      this.cloneCount = 0;
      this.isTransitioning = false;
      this.resizeTimer = null;
      this.originalItems = [...this.items];
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      if (!this.items.length && !this.tickerItems.length) return;
      this.init();
    }
    init() {
      createTicker(this.tickerItems, this.tickerSpeed);
      if (this.layout === 'slider') this.initStandardSlider();
      if (this.layout === 'arc_slider' && this.track && this.viewport) this.initArcSlider();
    }
    initStandardSlider() {
      if (!this.track) return;

      this.standardResizeTimer = null;
      this.standardCloneCount = 0;
      this.standardStep = 0;
      this.standardTransitioning = false;

      this.buildStandardInfiniteTrack();

      this.prevButton?.addEventListener('click', () => this.goToStandardPrev());
      this.nextButton?.addEventListener('click', () => this.goToStandardNext());

      addSwipe(this.viewport || this.track, {
        onLeft: () => this.goToStandardNext(),
        onRight: () => this.goToStandardPrev()
      });

      this.track.addEventListener('transitionend', () => this.handleStandardTransitionEnd());

      new ResizeObserver(() => {
        clearTimeout(this.standardResizeTimer);
        this.standardResizeTimer = setTimeout(() => {
          this.buildStandardInfiniteTrack();
        }, 120);
      }).observe(this.section);

      window.addEventListener('load', () => this.buildStandardInfiniteTrack(), { once: true });
    }

    getStandardVisibleCount() {
  return 3;
}

    getStandardGap() {
      return parseFloat(window.getComputedStyle(this.track).gap || window.getComputedStyle(this.track).columnGap || 0);
    }

    getStandardStep() {
      const first = this.track.querySelector('[data-review-gallery-item]');
      if (!first) return 0;
      return first.offsetWidth + this.getStandardGap();
    }

    clearStandardClones() {
      this.track.querySelectorAll('[data-review-gallery-clone="true"]').forEach((clone) => clone.remove());
    }

    getStandardSlides() {
      return Array.from(this.track.querySelectorAll('[data-review-gallery-item]'));
    }

    buildStandardInfiniteTrack() {
      this.clearStandardClones();

      this.originalItems = Array.from(
        this.track.querySelectorAll('[data-review-gallery-item]:not([data-review-gallery-clone="true"])')
      );

      const total = this.originalItems.length;
      const visible = this.getStandardVisibleCount();

      if (!total) return;

      this.standardCloneCount = Math.min(visible, total);

      if (total <= visible) {
        this.currentIndex = 0;
        this.standardStep = this.getStandardStep();

        this.track.style.transition = 'none';
        this.track.style.transform = 'translate3d(0,0,0)';

        this.originalItems.forEach((item, index) => {
          item.classList.toggle('is-active', index === Math.min(1, total - 1));
          item.setAttribute('aria-hidden', 'false');
          item.style.display = '';
        });

        if (this.prevButton) this.prevButton.disabled = true;
        if (this.nextButton) this.nextButton.disabled = true;
        return;
      }

      if (this.prevButton) this.prevButton.disabled = false;
      if (this.nextButton) this.nextButton.disabled = false;

      this.originalItems.slice(-this.standardCloneCount).forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-review-gallery-clone', 'true');
        this.track.insertBefore(clone, this.track.firstChild);
      });

      this.originalItems.slice(0, this.standardCloneCount).forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-review-gallery-clone', 'true');
        this.track.appendChild(clone);
      });

      this.standardStep = this.getStandardStep();
      this.currentIndex = this.standardCloneCount;

      this.jumpToStandardIndex(this.currentIndex);
      this.updateStandardActiveState();
    }

    setStandardTrackTransition(enabled) {
      this.track.style.transition = enabled
        ? 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)'
        : 'none';
    }

    jumpToStandardIndex(index) {
      this.setStandardTrackTransition(false);
      this.track.style.transform = `translate3d(${-index * this.standardStep}px, 0, 0)`;
      this.updateStandardActiveState();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.setStandardTrackTransition(true);
        });
      });
    }

    updateStandardSliderPosition() {
      this.track.style.transform = `translate3d(${-this.currentIndex * this.standardStep}px, 0, 0)`;
      this.updateStandardActiveState();
    }

    updateStandardActiveState() {
      const slides = this.getStandardSlides();
      const visible = this.getStandardVisibleCount();
      const activeOffset = visible === 1 ? 0 : 1;
      const activeIndex = this.currentIndex + activeOffset;

      slides.forEach((slide, index) => {
        slide.style.display = '';
        slide.setAttribute('aria-hidden', 'true');
        slide.classList.remove('is-active');

        if (index >= this.currentIndex && index < this.currentIndex + visible) {
          slide.setAttribute('aria-hidden', 'false');
        }

        if (index === activeIndex) {
          slide.classList.add('is-active');
        }
      });
    }

    resetStandardLoopIfNeeded() {
      const realCount = this.originalItems.length;

      if (this.currentIndex >= realCount + this.standardCloneCount) {
        this.currentIndex = this.standardCloneCount;
        this.jumpToStandardIndex(this.currentIndex);
      } else if (this.currentIndex < this.standardCloneCount) {
        this.currentIndex = realCount + this.currentIndex;
        this.jumpToStandardIndex(this.currentIndex);
      }
    }

    goToStandardNext() {
      if (this.standardTransitioning || this.originalItems.length <= this.getStandardVisibleCount()) return;
      this.standardTransitioning = true;
      this.currentIndex += 1;
      this.updateStandardSliderPosition();
    }

    goToStandardPrev() {
      if (this.standardTransitioning || this.originalItems.length <= this.getStandardVisibleCount()) return;
      this.standardTransitioning = true;
      this.currentIndex -= 1;
      this.updateStandardSliderPosition();
    }

    handleStandardTransitionEnd() {
      this.standardTransitioning = false;
      this.resetStandardLoopIfNeeded();
      this.updateStandardActiveState();
    }
    setArcVariables() {
      const d = this.section.dataset;
      if (d.sliderMaxWidth) this.section.style.setProperty('--slider-max-width', `${d.sliderMaxWidth}px`);
      if (d.slideWidth) this.section.style.setProperty('--slide-width', `${d.slideWidth}px`);
      if (d.slideGap) this.section.style.setProperty('--slide-gap', `${d.slideGap}px`);
      if (d.slideRadius) this.section.style.setProperty('--slide-radius', `${d.slideRadius}px`);
      if (d.arcRise) this.section.style.setProperty('--arc-rise', `${d.arcRise}px`);
      if (d.imageAspectRatio) this.section.style.setProperty('--media-aspect', d.imageAspectRatio);
    }
    getArcStep() {
      const first = this.track.querySelector('[data-review-gallery-item]');
      if (!first) return 0;
      return first.offsetWidth + parseFloat(window.getComputedStyle(this.track).columnGap || 0);
    }
    getVisibleArcCount() {
      const step = this.getArcStep();
      return step ? Math.max(1, Math.floor((this.viewport.offsetWidth + 8) / step)) : 1;
    }
    clearArcClones() {
      this.track.querySelectorAll('[data-review-gallery-clone="true"]').forEach((c) => c.remove());
    }
    getArcSlides() { return Array.from(this.track.querySelectorAll('[data-review-gallery-item]')); }
    updateArcSlideStates() {
      const slides = this.getArcSlides();
      if (!slides.length) return;
      const center = this.currentIndex + Math.floor(this.visibleSlides / 2);
      slides.forEach((s) => { s.classList.remove('is-center', 'is-left', 'is-right'); s.setAttribute('aria-hidden', 'true'); });
      if (slides[center]) { slides[center].classList.add('is-center'); slides[center].setAttribute('aria-hidden', 'false'); }
      if (slides[center - 1]) slides[center - 1].classList.add('is-left');
      if (slides[center + 1]) slides[center + 1].classList.add('is-right');
    }
    buildArcInfiniteTrack() {
      this.clearArcClones();
      this.originalItems = Array.from(this.track.querySelectorAll('[data-review-gallery-item]:not([data-review-gallery-clone="true"])'));
      this.visibleSlides = this.getVisibleArcCount();
      this.cloneCount = Math.min(this.visibleSlides + 1, this.originalItems.length);
      if (this.originalItems.length <= 1) {
        this.slideStep = this.getArcStep();
        this.currentIndex = 0;
        this.updateArcSlideStates();
        return;
      }
      this.originalItems.slice(-this.cloneCount).forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-review-gallery-clone', 'true');
        this.track.insertBefore(clone, this.track.firstChild);
      });
      this.originalItems.slice(0, this.cloneCount).forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-review-gallery-clone', 'true');
        this.track.appendChild(clone);
      });
      this.slideStep = this.getArcStep();
      this.currentIndex = this.cloneCount;
      this.jumpToArcIndex(this.currentIndex);
      this.updateArcSlideStates();
    }
    setArcTrackTransition(on) {
      this.track.style.transition = on ? 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
    }
    jumpToArcIndex(index) {
      this.setArcTrackTransition(false);
      this.track.style.transform = `translate3d(${-index * this.slideStep}px,0,0)`;
      this.updateArcSlideStates();
      requestAnimationFrame(() => requestAnimationFrame(() => this.setArcTrackTransition(true)));
    }
    updateArcSliderPosition() {
      this.track.style.transform = `translate3d(${-this.currentIndex * this.slideStep}px,0,0)`;
      this.updateArcSlideStates();
    }
    resetArcLoopIfNeeded() {
      const real = this.originalItems.length;
      if (this.currentIndex >= real + this.cloneCount) {
        this.currentIndex = this.cloneCount;
        this.jumpToArcIndex(this.currentIndex);
      } else if (this.currentIndex < this.cloneCount) {
        this.currentIndex = real + this.currentIndex;
        this.jumpToArcIndex(this.currentIndex);
      }
    }
    goToArcNext() {
      if (this.isTransitioning || this.originalItems.length <= 1) return;
      this.isTransitioning = true;
      this.currentIndex++;
      this.updateArcSliderPosition();
    }
    goToArcPrev() {
      if (this.isTransitioning || this.originalItems.length <= 1) return;
      this.isTransitioning = true;
      this.currentIndex--;
      this.updateArcSliderPosition();
    }
    handleTransitionEnd() {
      this.isTransitioning = false;
      this.resetArcLoopIfNeeded();
      this.updateArcSlideStates();
    }
    initArcSlider() {
      this.setArcVariables();
      this.buildArcInfiniteTrack();
      this.prevButton?.addEventListener('click', () => this.goToArcPrev());
      this.nextButton?.addEventListener('click', () => this.goToArcNext());
      this.track.addEventListener('transitionend', this.handleTransitionEnd);
      addSwipe(this.viewport, { onLeft: () => this.goToArcNext(), onRight: () => this.goToArcPrev() });
      new ResizeObserver(() => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.buildArcInfiniteTrack(), 120);
      }).observe(this.section);
      window.addEventListener('load', () => this.buildArcInfiniteTrack(), { once: true });
    }
  }

  class FactsSlider {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-facts-track]');
      this.slides = Array.from(section.querySelectorAll('[data-facts-slide]'));
      this.prevBtn = section.querySelector('[data-facts-prev]');
      this.nextBtn = section.querySelector('[data-facts-next]');
      this.currentIndex = 0;
      if (this.track && this.slides.length) this.init();
    }
    init() {
      this.prevBtn?.addEventListener('click', (e) => { e.preventDefault(); if (this.slides.length > 1) this.goTo(this.currentIndex - 1); });
      this.nextBtn?.addEventListener('click', (e) => { e.preventDefault(); if (this.slides.length > 1) this.goTo(this.currentIndex + 1); });
      addSwipe(this.track, {
        onLeft: () => { if (this.slides.length > 1) this.goTo(this.currentIndex + 1); },
        onRight: () => { if (this.slides.length > 1) this.goTo(this.currentIndex - 1); }
      });
      new ResizeObserver(() => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.update(false), 120);
      }).observe(this.section);
      this.update(false);
    }
    normalizeIndex(i) {
      const n = this.slides.length;
      if (!n) return 0;
      if (i < 0) return n - 1;
      if (i >= n) return 0;
      return i;
    }
    goTo(index) { this.currentIndex = this.normalizeIndex(index); this.update(true); }
    update(animate = true) {
      this.track.style.transition = animate ? 'transform 400ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
      this.track.style.transform = `translate3d(-${this.currentIndex * 100}%,0,0)`;
      const disabled = this.slides.length <= 1;
      this.slides.forEach((s, i) => {
        s.classList.toggle('is-active', i === this.currentIndex);
        s.setAttribute('aria-hidden', i === this.currentIndex ? 'false' : 'true');
      });
      if (this.prevBtn) this.prevBtn.disabled = disabled;
      if (this.nextBtn) this.nextBtn.disabled = disabled;
    }
  }

  class HomepageReveal {
    constructor(section) {
      this.section = section;
      this.logo = section.querySelector('[data-reveal-logo]');
      this.videoWrap = section.querySelector('.homepage-reveal__video-wrap');
      this.videoWrapImg = section.querySelector('.homepage-reveal__video-wrap img');
      if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;
      window.gsap.registerPlugin(window.ScrollTrigger);
      this.init();
    }
    init() {
      document.body.classList.add('homepage-reveal-active');
      this.tl = window.gsap.timeline({
        scrollTrigger: {
          trigger: this.section,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const done = self.progress >= 0.5;
            document.body.classList.toggle('homepage-reveal-active', !done);
            document.body.classList.toggle('homepage-reveal-complete', done);
            document.body.classList.toggle('homepage-reveal-logo-visible', self.progress >= 0.3);
          },
          onRefresh: () => this.cacheBounds()
        }
      });
      this.cacheBounds();
    }
    cacheBounds() {
      const headerLogo = document.querySelector('.header__logo__link');
      if (!headerLogo || !this.logo || !this.tl) return;
      const scale =
        headerLogo.getBoundingClientRect().width /
        this.logo.getBoundingClientRect().width;
      this.tl.clear();
      this.tl.to(this.logo, { scale, transformOrigin: '50% 0%', ease: 'none' }, 0);
      if (this.videoWrap) {
        this.tl.to(this.videoWrap, { x: '30vw', ease: 'none' }, 0);
      }
      if (this.videoWrapImg) {
        window.gsap.set(this.videoWrapImg, { y: 0, rotation: 0, scale: 1, transformOrigin: '50% 50%' });
        this.tl.to(this.videoWrapImg, { y: '-130vh', rotation: -8, scale: 0.2, ease: 'none', duration: 0.4 }, 0);
      }
    }
  }

  class TestimonialRotator {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('.testimonial-rotator__track');
      this.slides = Array.from(el.querySelectorAll('.testimonial-rotator__slide'));
      this.speed = (parseInt(el.dataset.speed, 10) || 3) * 1000;
      this.index = 1;
      this.interval = null;
      this.isTransitioning = false;
      if (!this.track || this.slides.length <= 1) return;
      this.setup();
      this.bindEvents();
      this.start();
    }
    setup() {
      const first = this.slides[0].cloneNode(true);
      const last = this.slides[this.slides.length - 1].cloneNode(true);
      first.setAttribute('aria-hidden', 'true');
      last.setAttribute('aria-hidden', 'true');
      this.track.insertBefore(last, this.slides[0]);
      this.track.appendChild(first);
      this.allSlides = Array.from(this.track.children);
      this.track.style.transition = 'none';
      this.track.style.transform = 'translateX(-100%)';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.track.style.transition = 'transform 0.6s ease';
      }));
    }
    move(dir) {
      if (this.isTransitioning) return;
      this.isTransitioning = true;
      this.index += dir;
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
    }
    next() { this.move(1); }
    prev() { this.move(-1); }
    handleTransitionEnd() {
      const last = this.allSlides.length - 1;
      if (this.index === last) { this.track.style.transition = 'none'; this.index = 1; }
      else if (this.index === 0) { this.track.style.transition = 'none'; this.index = last - 1; }
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.track.style.transition = 'transform 0.6s ease';
      }));
      this.isTransitioning = false;
    }
    start() { this.stop(); this.interval = setInterval(() => this.next(), this.speed); }
    stop() { if (this.interval) clearInterval(this.interval); }
    bindEvents() {
      this.track.addEventListener('transitionend', () => this.handleTransitionEnd());
      this.el.addEventListener('mouseenter', () => this.stop());
      this.el.addEventListener('mouseleave', () => this.start());
      addSwipe(this.el, {
        onLeft: () => { this.stop(); this.next(); this.start(); },
        onRight: () => { this.stop(); this.prev(); this.start(); }
      });
    }
  }

  class SidebarTabs {
    constructor(section) {
      this.section = section;
      this.triggers = Array.from(section.querySelectorAll('[data-tab-trigger]'));
      this.panels = Array.from(section.querySelectorAll('[data-tab-content]'));
      if (this.triggers.length && this.panels.length) this.bindEvents();
    }
    bindEvents() {
      this.triggers.forEach((t) => t.addEventListener('click', () => this.setActive(t.dataset.tabTarget)));
      const area = this.section.querySelector('[data-tab-content-area]') || this.section;
      addSwipe(area, {
        onLeft: () => {
          const i = this.triggers.findIndex((t) => t.classList.contains('is-active'));
          const next = this.triggers[(i + 1) % this.triggers.length];
          if (next) this.setActive(next.dataset.tabTarget);
        },
        onRight: () => {
          const i = this.triggers.findIndex((t) => t.classList.contains('is-active'));
          const prev = this.triggers[(i - 1 + this.triggers.length) % this.triggers.length];
          if (prev) this.setActive(prev.dataset.tabTarget);
        }
      });
    }
    setActive(id) {
      this.triggers.forEach((t) => t.classList.toggle('is-active', t.dataset.tabTarget === id));
      this.panels.forEach((p) => p.classList.toggle('is-active', p.dataset.tabId === id));
    }
  }

  class CustomArticleRecipeTabs {
    constructor(section) {
      this.section = section;
      this.buttons = Array.from(section.querySelectorAll('[data-tab-button]'));
      this.panels = Array.from(section.querySelectorAll('[data-tab-panel]'));
      if (this.buttons.length && this.panels.length) this.bindEvents();
    }
    bindEvents() {
      this.buttons.forEach((b) => b.addEventListener('click', () => this.setActiveTab(b.getAttribute('data-tab-target'))));
      const area = this.section.querySelector('[data-recipe-panel-area]') || this.section;
      addSwipe(area, {
        onLeft: () => {
          const i = this.buttons.findIndex((b) => b.classList.contains('is-active'));
          const next = this.buttons[(i + 1) % this.buttons.length];
          if (next) this.setActiveTab(next.getAttribute('data-tab-target'));
        },
        onRight: () => {
          const i = this.buttons.findIndex((b) => b.classList.contains('is-active'));
          const prev = this.buttons[(i - 1 + this.buttons.length) % this.buttons.length];
          if (prev) this.setActiveTab(prev.getAttribute('data-tab-target'));
        }
      });
    }
    setActiveTab(target) {
      this.buttons.forEach((b) => {
        const active = b.getAttribute('data-tab-target') === target;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.panels.forEach((p) => {
        const active = p.getAttribute('data-tab-panel') === target;
        p.classList.toggle('is-active', active);
        p.hidden = !active;
      });
    }
  }

  class InstaMarqueeHandler {
    constructor(fetchUrl) {
      this.fetchUrl = fetchUrl;
      this.container = document.getElementById('ig-feed');
      if (!this.fetchUrl) { console.error('InstaMarqueeHandler: beholdURL not defined.'); return; }
      if (!this.container) { console.error('InstaMarqueeHandler: #ig-feed not found.'); return; }
      this._init();
    }
    async _init() {
      try {
        const res = await fetch(this.fetchUrl);
        const data = await res.json();
        if (!data?.posts) return;
        const frag = document.createDocumentFragment();
        data.posts.forEach((post) => {
          const wrapper = this._createSlide(post);
          if (wrapper) frag.appendChild(wrapper);
        });
        this.container.appendChild(frag);
        this._addIconClasses();
        this.container.innerHTML += this.container.innerHTML;
      } catch (err) {
        console.error('InstaMarqueeHandler: fetch failed', err);
      }
    }
    _mediaHTML(permalink, url, type) {
      const inner = type === 'video'
        ? `<video autoplay muted loop playsinline name="media" alt="Instagram Video"><source src="${url}" type="video/mp4"></video>`
        : `<img src="${url}" width="800" height="auto" alt="Instagram Post"/>`;
      return `<a href="${permalink}" target="_blank" aria-label="View Instagram Post"><div class="ig-image-overlay"></div>${inner}</a>`;
    }
    _createSlide(post) {
      const type = post.mediaType.toLowerCase();
      let html = '';
      if (type === 'video') {
        html = this._mediaHTML(post.permalink, post.mediaUrl, 'video');
      } else if (type === 'image') {
        html = this._mediaHTML(post.permalink, post.mediaUrl, 'image');
      } else if (type === 'carousel_album' && post.children?.[0]) {
        const child = post.children[0];
        html = this._mediaHTML(post.permalink, child.mediaUrl, child.mediaType.toLowerCase());
      } else {
        console.error(`InstaMarqueeHandler: unsupported type "${type}"`);
        return null;
      }
      const slide = document.createElement('div');
      slide.className = 'ig-marquee-slide';
      slide.innerHTML = html;
      const wrapper = document.createElement('div');
      wrapper.className = 'ig-marquee-slide-wrapper';
      wrapper.style.aspectRatio = `1/${1 + 0.125 * Math.floor(4 * Math.random())}`;
      wrapper.appendChild(slide);
      return wrapper;
    }
    _addIconClasses() {
      const wrappers = [...this.container.querySelectorAll('.ig-marquee-slide-wrapper')];
      const mid = Math.floor(wrappers.length / 2);
      if (wrappers.length > 11) {
        wrappers.forEach((w, i) => { if (i % 4 === 0) w.classList.add('icon-added'); });
      } else if (wrappers.length > 4) {
        [wrappers[0], wrappers[mid]].forEach((w) => w.classList.add('icon-added'));
      } else if (wrappers[0]) {
        wrappers[0].classList.add('icon-added');
      }
    }
  }

  function initSubscriptionClickable(scope = document) {
  scope.querySelectorAll('.subscription_group .title_and_price_wrapper').forEach((wrapper) => {
    if (wrapper.dataset.clickableInitialized === 'true') return;
    wrapper.dataset.clickableInitialized = 'true';

    wrapper.addEventListener('click', (e) => {
      if (e.target.closest('select, option, a, input, label')) return;

      const radio = wrapper.querySelector('input[type="radio"]');
      if (!radio) return;

      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      radio.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });
}

  // ─── Section init helpers ────────────────────────────────────────────────────

  function initHomepageReveal(scope = document) {
    scope.querySelectorAll('[data-homepage-reveal]').forEach((el) => new HomepageReveal(el));
  }

  function initTextFloatAnimation(scope = document) {
    if (!window.gsap) return;
    scope.querySelectorAll('.text-float__container').forEach((el) => {
      if (el.dataset.textFloatInitialized === 'true') return;
      el.dataset.textFloatInitialized = 'true';
      new TextFloatAnimation(el);
    });
    window.ScrollTrigger?.refresh();
  }

  function initProductCollectionShowcase(scope = document) {
    scope.querySelectorAll('[data-product-collection-showcase]').forEach((el) => {
      if (el.dataset.showcaseInitialized === 'true') return;
      el.dataset.showcaseInitialized = 'true';
      new ProductCollectionShowcase(el);
    });
  }

  function initCircularCards(scope = document) {
    scope.querySelectorAll('[data-circular-cards-section]').forEach((el) => {
      if (el.dataset.circularReady === 'true') return;
      el.dataset.circularReady = 'true';
      new CircularCards(el);
    });
  }

  function initCustomTabs(scope = document) {
    scope.querySelectorAll('[data-custom-tabs]').forEach((el) => {
      if (el.classList.contains('is-custom-tabs-initialized')) return;
      el.classList.add('is-custom-tabs-initialized');
      new CustomTabs(el);
    });
  }

  function initReviewGalleryShowcases(scope = document) {
    scope.querySelectorAll('[data-review-gallery-showcase]').forEach((el) => {
      if (el.dataset.reviewGalleryInitialized === 'true') return;
      el.dataset.reviewGalleryInitialized = 'true';
      new ReviewGalleryShowcase(el);
    });
  }

  function initFactsSliders(scope = document) {
    scope.querySelectorAll('.brick__block--facts-slider').forEach((el) => {
      if (el.dataset.factsSliderInitialized === 'true') return;
      el.dataset.factsSliderInitialized = 'true';
      new FactsSlider(el);
    });
  }

  function initSidebarTabs(scope = document) {
    scope.querySelectorAll('[data-section-type="sidebar-tabs"]').forEach((el) => {
      if (el.sidebarTabsInstance) return;
      el.sidebarTabsInstance = new SidebarTabs(el);
    });
  }

  function initCustomArticleRecipeTabs(scope = document) {
    scope.querySelectorAll('[data-recipe-tabs]').forEach((el) => {
      if (el.dataset.recipeTabsInitialized === 'true') return;
      el.dataset.recipeTabsInitialized = 'true';
      new CustomArticleRecipeTabs(el);
    });
  }

  function initSlidingNav() {
    if (!window.matchMedia('(min-width: 1000px)').matches) return;
    const nav = document.querySelector('.header__menu');
    const focus = nav?.querySelector('.header_hover__bg');
    if (!nav || !focus) return;
    nav.classList.add('slider-nav');
    new SlidingNavAnimation(nav, focus);
  }

  function initTextFloatHeadings(scope = document) {
    scope.querySelectorAll('.text-float__heading strong').forEach((el) => {
      if (el.dataset.headingAnimated === 'true') return;
      el.dataset.headingAnimated = 'true';
      el.innerHTML = el.textContent
        .split('')
        .map((char) => char === ' ' ? ' ' : `<span class="letter">${char}</span>`)
        .join('');
      const letters = el.querySelectorAll('.letter');
      let hasPlayed = false;
      new IntersectionObserver(([entry], obs) => {
        if (!entry.isIntersecting || hasPlayed) return;
        hasPlayed = true;
        obs.disconnect();
        let index = 0;
        const interval = setInterval(() => {
          letters[index].classList.add('lit');
          index++;
          if (index >= letters.length) clearInterval(interval);
        }, 150);
      }, { threshold: 0.5 }).observe(el);
    });
  }


  function initCustomScripts(scope = document) {
    initHomepageReveal(scope);
    initProductCollectionShowcase(scope);
    initCircularCards(scope);
    initCustomTabs(scope);
    initReviewGalleryShowcases(scope);
    initFactsSliders(scope);
    initSidebarTabs(scope);
    initCustomArticleRecipeTabs(scope);
    initSubscriptionClickable(scope);
    idle(() => {
      initTextFloatAnimation(scope);
      initTextFloatHeadings(scope);
    });
  }


  function initFooterPops() {
    const trigger = document.querySelector('.green-wavy-border');
    const pops = document.querySelectorAll('.footer-pop');
    if (!trigger || !pops.length) return;
    new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        pops.forEach((el, i) => setTimeout(() => el.classList.add('is-visible'), 600 + i * 300));
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.3 }).observe(trigger);
  }

  function initTripleMediaReveal() {
    const elements = document.querySelectorAll('.triple-media-grid__overlay-title');
    if (!elements.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); });
    }, { threshold: 0.3 });
    elements.forEach((el) => obs.observe(el));
  }

  function initLooxRestructure() {
    let timer = null;
    let observer = null;

    function restructure() {
      const unstructured = document.querySelectorAll('.loox-rating-content:not(.is-structured)');
      if (!unstructured.length) return;

      unstructured.forEach((container) => {
        if (container.closest('.loox-widget')) return;
        const label = container.querySelector('.loox-rating-label');
        const stars = container.querySelectorAll('.loox-icon');
        if (!label || !stars.length) return;
        const count = label.textContent.replace(/[^\d]/g, '');
        if (!count) return;

        const reviewCount = document.createElement('div');
        reviewCount.className = 'loox-review-count';
        reviewCount.textContent = `${count} REVIEWS`;
        const starsWrap = document.createElement('div');
        starsWrap.className = 'loox-stars';
        stars.forEach((s) => starsWrap.appendChild(s));
        container.innerHTML = '';
        container.appendChild(reviewCount);
        container.appendChild(starsWrap);
        container.classList.add('is-structured');
      });

    
      if (!document.querySelector('.loox-rating-content:not(.is-structured)') && observer) {
        observer.disconnect();
        observer = null;
      }
    }

    restructure();
    window.addEventListener('load', restructure, { once: true });


    const observerTarget = document.querySelector('.product-info, main, #MainContent') || document.body;

    observer = new MutationObserver((mutations) => {
      const hasLoox = mutations.some((m) =>
        [...m.addedNodes].some(
          (n) => n.nodeType === 1 && (n.classList?.contains('loox-rating-content') || n.querySelector?.('.loox-rating-content'))
        )
      );
      if (!hasLoox) return; 

      clearTimeout(timer);
      timer = setTimeout(restructure, 80);
    });

    observer.observe(observerTarget, { childList: true, subtree: true });
  }

  function initSubscriptionPicker() {
    document.addEventListener('change', (e) => {
      const picker = e.target.closest('.custom-subscription-picker');
      if (!picker) return;
      const form = picker.closest('[data-product-panel]')?.querySelector('form[data-product-form]')
        || picker.closest('.product-form')?.querySelector('form');
      const sellingPlanInput = form?.querySelector('[data-selling-plan-input]');
      if (!sellingPlanInput) return;
      const { target } = e;
      const oneTime = picker.querySelector('[data-purchase-type="one_time"]');
      const subscription = picker.querySelector('[data-purchase-type="subscription"]');
      const select = picker.querySelector('[data-selling-plan-select]');
      const priceEl = picker.querySelector('[data-subscription-price]');
      if (target === oneTime) {
        sellingPlanInput.value = '';
      } else if (target === subscription) {
        sellingPlanInput.value = select ? select.value : '';
      } else if (target === select) {
        if (subscription) subscription.checked = true;
        sellingPlanInput.value = select.value;
        const opt = select.options[select.selectedIndex];
        if (priceEl && opt?.dataset.price) priceEl.textContent = opt.dataset.price;
      }
    });
  }

  function initBlogTagFilter() {
    document.addEventListener('click', (e) => {
      const filter = e.target.closest('.blog-tag-filter');
      const toggle = e.target.closest('.blog-tag-filter .custom-tabs__link--button');
      document.querySelectorAll('.blog-tag-filter').forEach((f) => {
        if (f === filter) return;
        f.classList.remove('is-open');
        f.querySelector('.custom-tabs__link--button')?.setAttribute('aria-expanded', 'false');
      });
      if (toggle && filter) {
        e.preventDefault();
        const open = filter.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    });
  }

  function initFeaturePills() {
    document.addEventListener('click', (e) => {
      const pill = e.target.closest('[data-feature-pill]');
      if (!pill) return;
      const row = pill.closest('[data-feature-icons-row]');
      if (!row) return;
      row.querySelectorAll('[data-feature-pill]').forEach((p) => {
        p.classList.remove('is-active');
        p.setAttribute('aria-expanded', 'false');
      });
      pill.classList.add('is-active');
      pill.setAttribute('aria-expanded', 'true');
    });
  }

  function initTestimonialRotators() {
    document.querySelectorAll('[data-rotator]').forEach((el) => new TestimonialRotator(el));
  }

  function initInstaMarquee() {
    if (typeof beholdURL === 'undefined' || !beholdURL) {
      console.warn('InstaMarqueeHandler: beholdURL not defined. Skipping.');
      return;
    }
    new InstaMarqueeHandler(beholdURL);
  }

  function initMobileDropdowns() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.mobile-dropdown__trigger');
      if (!trigger) return;
      const wrapper = trigger.closest('.mobile-dropdown');
      const panel = wrapper.querySelector('.mobile-dropdown__panel');
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      wrapper.closest('.sliderow__links, .mobile__menu__dropdown')
        ?.querySelectorAll('.mobile-dropdown__trigger[aria-expanded="true"]')
        .forEach((open) => {
          if (open === trigger) return;
          open.setAttribute('aria-expanded', 'false');
          open.closest('.mobile-dropdown').querySelector('.mobile-dropdown__panel').setAttribute('hidden', '');
        });
      trigger.setAttribute('aria-expanded', String(!isOpen));
      if (isOpen) { panel.setAttribute('hidden', ''); }
      else { panel.removeAttribute('hidden'); }
    });
  }

  function initHoverVideos() {
    document.querySelectorAll('.article__image, .article-image').forEach((card) => {
      const video = card.querySelector('.article__hover-video__player');
      if (!video) return;
      card.addEventListener('mouseenter', () => video.play());
      card.addEventListener('mouseleave', () => { video.pause(); video.currentTime = 0; });
    });
  }

  function initMobileHighlightsDropdown() {
    if (window.innerWidth > 767) return;
    const container = document.querySelector('.highlights__items');
    if (!container) return;
    const items = container.querySelectorAll('.highlights__item');
    items.forEach((item, i) => { if (i > 0) item.style.display = 'none'; });
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:relative;flex-shrink:0;';
    const btnItem = document.createElement('div');
    btnItem.className = 'highlights__item highlights__item--rectangle';
    const btnInner = document.createElement('button');
    btnInner.className = 'highlights__item-inner highlights__item-link';
    btnInner.innerHTML = '<div class="highlights__item__background"></div><div class="highlights__item__content body-medium"><span style="font-weight:700; text-transform: uppercase;">category</span></div>';
    btnItem.appendChild(btnInner);
    const dropdown = document.createElement('div');
    dropdown.style.cssText = 'display:none;position:absolute;top:100%;left:0;background:var(--color-beige);border:2px solid var(--color-dark-orange);border-radius: 0 0 0.625rem 0.625rem;min-width:180px;z-index:100;overflow:hidden;';
    items.forEach((item, i) => {
      if (i === 0) return;
      const link = item.querySelector('a');
      if (!link) return;
      const text = link.querySelector('.highlights__item__content span')?.textContent || '';
      const href = link.getAttribute('href');
      const row = document.createElement('div');
      row.className = 'highlights__item highlights__item--rectangle';
      row.style.cssText = 'border-radius:0 !important;border: 0 !important;';
      row.innerHTML = `<a href="${href}" class="highlights__item-inner highlights__item-link" style="opacity: 1 !important;"><div class="highlights__item__background"></div><div class="highlights__item__content body-medium"><span>${text}</span></div></a>`;
      if (window.location.pathname === href) row.querySelector('a').classList.add('is-active');
      dropdown.appendChild(row);
    });
    if (dropdown.lastChild) dropdown.lastChild.style.borderBottom = 'none';
    btnInner.addEventListener('click', () => {
      dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
    });
    document.addEventListener('click', (e) => { if (!wrapper.contains(e.target)) dropdown.style.display = 'none'; });
    wrapper.appendChild(btnItem);
    wrapper.appendChild(dropdown);
    container.appendChild(wrapper);
  }

  function initRecipeFruitFilter() {
    const filters = document.querySelectorAll('.recipe-fruit-filter');
    if (!filters.length) return;

    function closeFilter(filter) {
      const button = filter.querySelector('.recipe-fruit-filter__toggle');
      const menu = filter.querySelector('.recipe-fruit-filter__menu');
      if (!button || !menu) return;
      filter.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
      menu.classList.remove('popout-list--visible');
    }

    function openFilter(filter) {
      const button = filter.querySelector('.recipe-fruit-filter__toggle');
      const menu = filter.querySelector('.recipe-fruit-filter__menu');
      if (!button || !menu) return;
      filter.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      menu.hidden = false;
      menu.classList.add('popout-list--visible');
    }

    filters.forEach((filter) => {
      const button = filter.querySelector('.recipe-fruit-filter__toggle');
      if (!button) return;
      closeFilter(filter);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const isOpen = filter.classList.contains('is-open');
        filters.forEach((item) => closeFilter(item));
        if (!isOpen) openFilter(filter);
      });
    });

    document.addEventListener('click', (event) => {
      filters.forEach((filter) => { if (!filter.contains(event.target)) closeFilter(filter); });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') filters.forEach((filter) => closeFilter(filter));
    });
  }

  function initDefaultPurchaseType() {
  const oneTime = document.querySelector('[data-radio-type="one_time_purchase"]');
  const subscription = document.querySelector('[data-radio-type="selling_plan_group"]');

  function clearSellingPlan() {
    document.querySelectorAll('.selected-selling-plan-id, [name="selling_plan"]').forEach(input => {
      input.value = '';
      input.removeAttribute('value');
    });
  }

  function setSellingPlan(value) {
    document.querySelectorAll('.selected-selling-plan-id, [name="selling_plan"]').forEach(input => {
      input.value = value;
      input.setAttribute('value', value);
    });
  }

  if (oneTime) {
    oneTime.checked = true;
    oneTime.setAttribute('checked', 'checked');
  }

  if (subscription) {
    subscription.checked = false;
    subscription.removeAttribute('checked');
  }

  clearSellingPlan();

  document.querySelectorAll('[data-radio-type]').forEach(radio => {
    radio.addEventListener('change', () => {
      if (radio.dataset.radioType === 'one_time_purchase') {
        clearSellingPlan();
      }

      if (radio.dataset.radioType === 'selling_plan_group') {
        setSellingPlan(radio.dataset.sellingPlanId || '');
      }
    });
  });

  document.addEventListener('submit', event => {
    const form = event.target;

    if (!form.matches('form[action*="/cart/add"]')) return;

    const checkedPurchaseOption = form.querySelector('[data-radio-type]:checked');

    if (checkedPurchaseOption?.dataset.radioType === 'one_time_purchase') {
      clearSellingPlan();
    }
  }, true);
}

  // ─── Bootstrap  ────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {
    // HomepageReveal 
    initHomepageReveal(document);

    initProductCollectionShowcase(document);
    initCircularCards(document);
    initCustomTabs(document);
    initReviewGalleryShowcases(document);
    initFactsSliders(document);
    initSidebarTabs(document);
    initCustomArticleRecipeTabs(document);


    initSubscriptionPicker();
    initSubscriptionClickable(document);
    initBlogTagFilter();
    initFeaturePills();
    initMobileDropdowns();
    initHoverVideos();
    initMobileHighlightsDropdown();
    initFooterPops();
    initRecipeFruitFilter();
    initDefaultPurchaseType();


    idle(() => {
      initTextFloatAnimation(document);
      initTextFloatHeadings(document);
      initTripleMediaReveal();
      initSlidingNav();
      initTestimonialRotators();
      initLooxRestructure();
      initInstaMarquee();
    });
  });

  document.addEventListener('shopify:section:load', (e) => initCustomScripts(e.target));
})();